import { useDataProvider, useNotify } from 'react-admin';
import { useEffect, useState } from "react";
import InputFieldCustom from '../Commons/InputFieldCustom'
import { useMediaQuery } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import BreadCrumbCustom from '../Commons/BreadCrum';
import historyTracker from '../Commons/historyTracker';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import AuthNotification from '../Commons/AuthNotification';
import { Style } from '../Commons/Styles.jsx';
import BottomToolBar from '../Commons/BottomToolBar';
import InternalMenu from '../Commons/InternalMenu';
// TODO: reemplazar por el ícono definitivo de Bancos cuando esté disponible
import banks from '../Commons/Icons/building.svg';
import requestAuthorization from '../Commons/Icons/colocacion.svg';
// TODO: reemplazar por el ícono definitivo de Imprimir Solicitudes cuando esté disponible
import printRequests from '../Commons/Icons/contabilidad.svg';

import { hasPermissions } from '../Commons/CompanyAuth';

// Mismo catalogo de tipo_pago que usa sp_cblistasolicitudesautorizacion (CASE sin ELSE, ver
// RequestAuthorizationsList.jsx / Banksapi.cs): CL/PR/OT/CC/RM. Aca se recibe el codigo
// numerico crudo (tipo_pago de sp_devuelve_cbmaesolicitud), asi que se mapea igual en el
// front en vez de duplicar el CASE en el backend.
const PaymentTypeNames = {
    0: 'Cliente',
    1: 'Proveedor',
    2: 'Otro',
    3: 'Cuenta Corriente',
    4: 'Remesa',
};

// Mismo catalogo que sp_cblistasolicitudesautorizacion/sp_get_cbmaesolicitud para
// codigo_tipo_mov (CH/DE/ND/NC).
const MovementTypeNames = {
    1: 'Cheque',
    2: 'Deposito',
    3: 'Nota de Debito',
    4: 'Nota de Credito',
};

// estatus de cbmaesolicitud: sp_devuelve_cbmaesolicitud excluye 99 (Anulada), asi que solo
// llegan 0-4 (ver DescripcionEstado en sp_get_cbmaesolicitud, no elegida para esta pantalla
// pero misma tabla de valores).
const StatusNames = {
    0: 'Nueva',
    1: 'Por Autorizar',
    2: 'Autorizada',
    3: 'Ejecutada',
    4: 'Rechazada',
};

// El "id" del recurso es compuesto "codigo_cuenta-numero_solicitud" (ver comentario en
// Banksapi.cs); se extrae codigo_cuenta del propio id para poder buscar la cuenta bancaria
// en el catalogo, ya que sp_devuelve_cbmaesolicitud no devuelve numero_cuenta/nombre_cuenta
// (solo trae corre_cbcajas, que es la caja de fondeo si aplica, no la cuenta bancaria de la
// solicitud).
const getAccountCodeFromId = (id) => {
    const parts = (id || '').split('-');
    return parts.length === 2 ? parseInt(parts[0], 10) : null;
};

const formatDate = (value) => value ? new Date(value).toLocaleDateString('es-GT') : '';

const formatMoney = (value) =>
    value === null || value === undefined
        ? ''
        : parseFloat(value).toLocaleString('es-GT', { style: 'decimal', minimumFractionDigits: 2 });

// Detalle de UNA solicitud pendiente de autorizar (sp_devuelve_cbmaesolicitud, via
// Get_Bank_RequestAuthorization/Banksapi.cs). El "id" del recurso es compuesto
// ("codigo_cuenta-numero_solicitud", ver comentario en Banksapi.cs) porque la SP exige
// ambos valores para ubicar la fila. Incluye las acciones de Autorizar/Rechazar
// (sp_cbautorizar_solicitud/sp_cbrechaza_solicitud, via Bank_RequestAuthorization_Authorize/
// Reject en Banksapi.cs).
const RequestAuthorizationShow = () => {
    const { id } = useParams();
    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const location = useLocation();
    const dataProvider = useDataProvider();
    const notify = useNotify();

    const [openAuth, setOpenAuth] = useState(false);
    const [auth, setAuth] = useState(false);
    // Mientras esto es true se muestra el spinner de carga inicial (ver el bloque
    // "initialLoading" en el return): sin este estado la ventana se veia en blanco
    // (solo el breadcrumb) mientras se traian los permisos y el detalle de la solicitud.
    const [initialLoading, setInitialLoading] = useState(true);
    const [authAuthorize, setAuthAuthorize] = useState(false);
    const [authReject, setAuthReject] = useState(false);
    const [authorizing, setAuthorizing] = useState(false);
    const [rejecting, setRejecting] = useState(false);
    // Codigo numerico crudo de estatus (no el texto ya mapeado de "status", mas abajo):
    // los botones de Autorizar/Rechazar solo tienen sentido con la solicitud en estatus 1
    // (Por Autorizar) -- ver el comentario de Bank_RequestAuthorization_Authorize en
    // Banksapi.cs sobre por que esa SP es un toggle y no una accion de un solo sentido.
    const [statusCode, setStatusCode] = useState(null);
    const [account, setAccount] = useState('');
    const [requestNumber, setRequestNumber] = useState('');
    const [documentDate, setDocumentDate] = useState('');
    const [promisedPaymentDate, setPromisedPaymentDate] = useState('');
    const [paymentType, setPaymentType] = useState('');
    const [movementType, setMovementType] = useState('');
    const [payableTo, setPayableTo] = useState('');
    const [amount, setAmount] = useState('');
    const [amountML, setAmountML] = useState('');
    const [referenceRate, setReferenceRate] = useState('');
    const [concept, setConcept] = useState('');
    const [requestDate, setRequestDate] = useState('');
    const [moduleName, setModuleName] = useState('');
    const [status, setStatus] = useState('');
    const [user, setUser] = useState('');
    const [movementDate, setMovementDate] = useState('');

    useEffect(() => {
        (async () => {
            try {
                // Recurso de permisos nuevo, ver mismo comentario en RequestAuthorizationsList.jsx.
                // Se piden las 3 acciones de una vez (mismo patron que AccountingExpensesList con
                // "list,create"): authdata.data[0]/[1]/[2] son list/authorize/reject en ese orden.
                const authdata = { data: hasPermissions('BankRequestAuthorization', 'list,authorize,reject') };
                if (authdata.data.length > 0) {
                    setAuth(authdata.data[0]);
                    setOpenAuth(!authdata.data[0]);
                    setAuthAuthorize(authdata.data[1]);
                    setAuthReject(authdata.data[2]);

                    if (authdata.data[0]) {
                        // provider/bankaccounts (sp_lista_cbtaban_simple) es el catalogo de cuentas
                        // bancarias de la empresa que ya usa ProviderPayableInvoiceShow.jsx para su
                        // combo de "Cuenta de Pago"; se reutiliza aca en vez de agregar una consulta
                        // nueva al backend. Ojo: su campo "id" es el correlativo propio de cbtaban
                        // (PK interna), NO codigo_cuenta -- para encontrar la cuenta de esta solicitud
                        // hay que comparar contra "accountCode" (el campo que sí es codigo_cuenta).
                        const accountCode = getAccountCodeFromId(id);
                        const [{ data }, bankAccountsData] = await Promise.all([
                            dataProvider.getOne('bank/requestauthorization', { id }),
                            dataProvider.getList('provider/bankaccounts', { filter: {}, pagination: {}, sort: {} }).catch(() => ({ data: [] })),
                        ]);

                        historyTracker.addPath(location.pathname, `Solicitud ${data.requestNumber || ''}`, 3);

                        const matchedAccount = bankAccountsData.data.find((item) => item.accountCode === accountCode);
                        setAccount(matchedAccount ? `${matchedAccount.accountNumber} - ${matchedAccount.accountName}` : '');
                        setStatusCode(data.status !== null && data.status !== undefined ? data.status : null);
                        setRequestNumber(data.requestNumber?.toString() || '');
                        setDocumentDate(formatDate(data.documentDate));
                        setPromisedPaymentDate(formatDate(data.promisedPaymentDate));
                        setPaymentType(PaymentTypeNames[data.paymentType] || '');
                        setMovementType(MovementTypeNames[data.movementTypeCode] || '');
                        setPayableTo(data.payableTo || '');
                        setAmount(formatMoney(data.amount));
                        setAmountML(formatMoney(data.amountML));
                        setReferenceRate(data.referenceRate !== null && data.referenceRate !== undefined ? parseFloat(data.referenceRate).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 8 }) : '');
                        setConcept(data.concept || '');
                        setRequestDate(formatDate(data.requestDate));
                        setModuleName(data.moduleName || '');
                        setStatus(StatusNames[data.status] || '');
                        setUser(data.user || '');
                        setMovementDate(formatDate(data.movementDate));
                    }
                } else {
                    setAuth(false);
                    setOpenAuth(true);
                }
            }
            catch (error) {
                console.log(error);
            }
            finally {
                setInitialLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataProvider, location.pathname, id]);

    // sp_cbautorizar_solicitud/sp_cbrechaza_solicitud dejan la solicitud fuera de estatus 1
    // (Autorizada, Rechazada, o de vuelta a Nueva si la integracion contable no cuadraba), asi
    // que en los tres casos ya no pertenece al listado de pendientes -- se regresa ahi en vez
    // de quedarse en esta vista, igual que RegisterInvoice en ProviderInvoiceShow.jsx.
    const handleAuthorize = async () => {
        setAuthorizing(true);
        try {
            await dataProvider.create(`bank/requestauthorization/${id}/authorize`, { data: {} });
            notify('Solicitud autorizada correctamente.', { type: 'success' });
            handleRedirectRequestAuthorizations();
        } catch (error) {
            notify(error?.message || 'No se pudo autorizar la solicitud.', { type: 'error' });
            setAuthorizing(false);
        }
    };

    const handleReject = async () => {
        setRejecting(true);
        try {
            await dataProvider.create(`bank/requestauthorization/${id}/reject`, { data: {} });
            notify('Solicitud rechazada correctamente.', { type: 'success' });
            handleRedirectRequestAuthorizations();
        } catch (error) {
            notify(error?.message || 'No se pudo rechazar la solicitud.', { type: 'error' });
            setRejecting(false);
        }
    };

    const handleRedirectRequestAuthorizations = () => {
        navigate(`/bank/requestauthorization`);
    };

    // Bancos > Imprimir Solicitudes: por ahora solo navega el menú (mismo criterio que se
    // uso para Autorizar Solicitudes antes de construir su pantalla); la vista real se
    // conecta cuando exista el recurso correspondiente en el API y el Resource en App.jsx.
    const handleRedirectPrintRequests = () => {
        navigate(`/bank/printrequests`);
    };

    const [openBanks, setOpenBanks] = useState(true);

    const handleClickOpenBanks = () => {
        setOpenBanks(!openBanks);
    };

    const busy = authorizing || rejecting;
    // Solo tienen sentido con la solicitud todavia en estatus 1 (Por Autorizar), ver
    // comentario de statusCode mas arriba.
    const canActOnRequest = statusCode === 1;

    const menus = [{
        icon: <img src={banks} alt="logo" style={Style.MenuLogo} />, name: 'Bancos',
        open: openBanks,
        openAction: handleClickOpenBanks,
        actions: [
            { icon: <img src={requestAuthorization} alt="logo" style={Style.MenuLogo} />, name: 'Autorizar Solicitudes', onClick: handleRedirectRequestAuthorizations },
            { icon: <img src={printRequests} alt="logo" style={Style.MenuLogo} />, name: 'Imprimir Solicitudes', onClick: handleRedirectPrintRequests },
        ]
    }];

    return (
        <div class="container">
            {/* A diferencia del "Loading" absoluto que ya usan ProviderInvoiceShow/
                ProviderPayableInvoiceShow (que solo tapa el propio contenedor de la
                pantalla), este es "position: fixed" a proposito: autorizar/rechazar dispara
                movimientos contables/bancarios reales, y se pidio explicitamente bloquear
                TODA la pantalla (sidebar y topbar de react-admin incluidos, no solo este
                formulario) mientras la solicitud esta en vuelo, para que no se pueda navegar
                a otra pantalla a medio pedido. zIndex por encima del Drawer/AppBar de MUI
                (que rondan 1200-1300) para que de verdad quede arriba de todo. */}
            {busy ? (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.6)',
                    zIndex: 2000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <CircularProgress size="8rem" />
                </div>
            ) : null}
            <BreadCrumbCustom history={historyTracker.getHistory()} />
            {initialLoading ? (
                // Spinner de carga inicial: reutiliza la misma clase "Loading" (index.css) y
                // el mismo CircularProgress que ya se usa en otras pantallas para el
                // guardado, pero aqui cubre la espera de auth + detalle de la solicitud, que
                // antes dejaba la pantalla en blanco.
                <div class="Loading" style={{ display: 'block' }}>
                    <CircularProgress size="15rem" />
                </div>
            ) : auth ? (
                <div class="edit">
                    {!isSmall ? (
                        <div class="container" style={{ opacity: busy ? 0.3 : 1 }}>
                            <div class="row">
                                <InputFieldCustom readOnly value={requestNumber} setValue={setRequestNumber} col="col-3" width="95%" label="No. Solicitud" type="text" />
                                <InputFieldCustom readOnly value={account} setValue={setAccount} col="col-3" width="95%" label="Cuenta Bancaria" type="text" />
                                <InputFieldCustom readOnly value={movementType} setValue={setMovementType} col="col-3" width="95%" label="Tipo" type="text" />
                                <InputFieldCustom readOnly value={paymentType} setValue={setPaymentType} col="col-3" width="95%" label="Tipo de Pago" type="text" />
                            </div>
                            <div class="row">
                                <InputFieldCustom readOnly value={status} setValue={setStatus} col="col-3" width="95%" label="Estado" type="text" />
                                <InputFieldCustom readOnly value={documentDate} setValue={setDocumentDate} col="col-3" width="95%" label="Fecha Documento" type="text" />
                                <InputFieldCustom readOnly value={promisedPaymentDate} setValue={setPromisedPaymentDate} col="col-3" width="95%" label="Fecha Promesa de Pago" type="text" />
                                <InputFieldCustom readOnly value={requestDate} setValue={setRequestDate} col="col-3" width="95%" label="Fecha Solicitud" type="text" />
                            </div>
                            <div class="row">
                                <InputFieldCustom readOnly value={payableTo} setValue={setPayableTo} col="col-3" width="95%" label="Girado" type="text" />
                                <InputFieldCustom readOnly value={amount} setValue={setAmount} col="col-3" width="95%" label="Monto" type="text" />
                                <InputFieldCustom readOnly value={amountML} setValue={setAmountML} col="col-3" width="95%" label="Monto Moneda Local" type="text" />
                                <InputFieldCustom readOnly value={referenceRate} setValue={setReferenceRate} col="col-3" width="95%" label="Tasa de Referencia" type="text" />
                            </div>
                            <div class="row">
                                <InputFieldCustom readOnly value={moduleName} setValue={setModuleName} col="col-3" width="95%" label="Módulo" type="text" />
                                <InputFieldCustom readOnly value={user} setValue={setUser} col="col-3" width="95%" label="Usuario" type="text" />
                                <InputFieldCustom readOnly value={movementDate} setValue={setMovementDate} col="col-3" width="95%" label="Fecha de Movimiento" type="text" />
                            </div>
                            <div class="row">
                                <InputFieldCustom readOnly value={concept} setValue={setConcept} col="col-12" width="95%" label="Concepto" type="text" component="textarea" referenceCol="col-3" />
                            </div>

                            <div style={{ marginTop: '2em' }}>
                                <BottomToolBar disabled={busy} buttons={[
                                    { auth: authAuthorize && canActOnRequest, action: handleAuthorize, name: authorizing ? 'Autorizando...' : 'Autorizar' },
                                    { auth: authReject && canActOnRequest, action: handleReject, name: rejecting ? 'Rechazando...' : 'Rechazar' }
                                ]} menu={<InternalMenu menus={menus} />} />
                            </div>
                        </div>
                    ) : (
                        <div class="container">
                        </div>
                    )}
                </div>) : (<AuthNotification openerror={openAuth} severity={'error'} message={'No posee los permisos para poder interactuar con esta vista'} />)
            }
        </div>
    )
}

export default RequestAuthorizationShow;
